import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const APP_STORE_LINK = process.env.REACT_APP_JCAL_APP_STORE_LINK || 'https://apps.apple.com/us/app/jcal-la/id6575360031';
const PLAY_STORE_LINK = process.env.REACT_APP_JCAL_PLAY_STORE_LINK || 'https://play.google.com/store/apps/details?id=happtrak.jcal.app';
const CAROUSEL_INTERVAL_MS = 8000; // fallback for slides without video
const SLIDE_END_DELAY_MS = 1500;   // hold on last frame before advancing

const SLIDES = [
    { label: "What's On", color: '#e8fff4', video: '/whats_on.mp4', bubbles: [
        { text: 'Personalized recommendations that learn from your preferences and browsing history.', x: 35, y: 30, delay: 0, duration: 6},
        { text: 'Browse events by Upcoming and Recently Added too.',    x: 45, y: 45, delay: 4, duration: 6},
        //{ text: 'Synced',  x: 20, y: 68, delay: 1.6 },
    ]},
    { label: 'Calendar', color: '#e8f4ff', video: '/calendar.mp4', bubbles: [
        { text: 'Browse events by Month, Week, and Day.', x: 50, y: 25, delay: 0, duration: 6 },
        //{ text: 'Yom Tov', x: 18, y: 55, delay: 0.8 },
    ]},
    { label: 'Map', color: '#f0e8ff', video: '/map.mp4', bubbles: [
        { text: 'Map events based on neighborhoods in your city.', x: 35, y: 20, delay: 0, duration: 5 },
        { text: 'Available in NYC and Los Angeles.', x: 50, y: 80, delay: 6 },
    ]},
    { label: 'Meet Moishe', color: '#fff8e8', video: '/rabbi_search.mp4', bubbles: [
        { text: 'Meet Moishe, your personal guide to Jewish events and experiences.', x: 50, y: 40, delay: 0 },
        { text: 'Ask for Pickleball...', x: 50, y: 35, delay: 3 },
        { text: 'And YE SHALL RECEIVE!', x: 50, y: 30, delay: 7 },
        { text: '(Other Jewish experiences besides Pickleball also available)', x: 50, y: 35, delay: 10.5 },



    ]},
    { label: 'Friends', color: '#f4f4e8', image: '/friends.png', bubbles: [
        { text: 'Create a social profile so your friends can find you.', x: 50, y: 40, delay: 0, duration: 5 },
        { text: 'Private, Social, and Public modes available to personalize your visibility.', x: 70, y: 70, delay: 4 },
    ]},
];

function Carousel() {
    const [active, setActive] = useState(0);
    const trackRef = useRef(null);
    const timerRef = useRef(null);
    const activeRef = useRef(0);
    const videoRefs = useRef({});

    // Slides are 240px wide with 28px gap — each is 268px further right.
    const getSlideScrollLeft = useCallback((i) => i * (240 + 28), []);

    // Keep activeRef in sync so onEnded closures see the current slide.
    useEffect(() => { activeRef.current = active; }, [active]);

    const scrollToSlide = useCallback((next) => {
        const track = trackRef.current;
        if (!track) return;
        if (next === 0) track.scrollLeft = getSlideScrollLeft(0);
        else track.scrollTo({ left: getSlideScrollLeft(next), behavior: 'smooth' });
    }, [getSlideScrollLeft]);

    const advance = useCallback((fromSlide, delay) => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (activeRef.current !== fromSlide) return; // user navigated away
            const next = (fromSlide + 1) % SLIDES.length;
            setActive(next);
            activeRef.current = next;
            scrollToSlide(next);
        }, delay);
    }, [scrollToSlide]);

    // When a video slide becomes active, restart the video from the beginning.
    useEffect(() => {
        const slide = SLIDES[active];
        if (!slide.video) return;
        const video = videoRefs.current[active];
        if (video) {
            video.currentTime = 0;
            video.play().catch(() => {});
        }
    }, [active]);

    // Image slides: schedule advance on a fixed timer whenever they become active.
    useEffect(() => {
        if (SLIDES[active].video) return;
        advance(active, CAROUSEL_INTERVAL_MS);
        return () => clearTimeout(timerRef.current);
    }, [active, advance]);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;
        const onScroll = () => {
            const idx = Math.min(Math.max(Math.round(track.scrollLeft / (240 + 28)), 0), SLIDES.length - 1);
            setActive(idx);
            activeRef.current = idx;
        };
        track.addEventListener('scroll', onScroll, { passive: true });
        return () => track.removeEventListener('scroll', onScroll);
    }, []);

    const goTo = useCallback((i) => {
        const next = (i + SLIDES.length) % SLIDES.length;
        clearTimeout(timerRef.current);
        setActive(next);
        activeRef.current = next;
        if (trackRef.current) trackRef.current.scrollLeft = getSlideScrollLeft(next);
    }, [getSlideScrollLeft]);

    const pauseAutoplay = () => clearTimeout(timerRef.current);

    return (
        <div className="jcalm-carousel-wrap" onPointerDown={pauseAutoplay}>
            <div className="jcalm-carousel-track" ref={trackRef}>
                {SLIDES.map((s, i) => (
                    <div className="jcalm-slide" key={i}>
                        <div className="jcalm-phone">
                            <div className="jcalm-phone-notch" />
                            <div className="jcalm-phone-screen" style={{ background: s.color }}>
                                {s.video ? (
                                    <video
                                        ref={(el) => { if (el) videoRefs.current[i] = el; }}
                                        src={s.video}
                                        muted
                                        playsInline
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                        onEnded={(e) => {
                                            // Seek to last frame so browser doesn't show black.
                                            e.target.currentTime = Math.max(0, e.target.duration - 0.033);
                                            advance(i, SLIDE_END_DELAY_MS);
                                        }}
                                    />
                                ) : s.image ? (
                                    <img
                                        src={s.image}
                                        alt={s.label}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                    />
                                ) : (
                                    <span className="jcalm-phone-label">{s.label}</span>
                                )}
                            </div>
                            <div className="jcalm-phone-home" />
                            <div className="jcalm-screen-bubbles" aria-hidden="true">
                                {(s.bubbles || []).map((bubble, bubbleIndex) => (
                                    <span
                                        className="jcalm-screen-bubble"
                                        key={`${i}-${bubble.text}-${bubbleIndex}-${active}`}
                                        style={{
                                            left: `${bubble.x}%`,
                                            top: `${bubble.y}%`,
                                            animationDelay: `${bubble.delay}s`,
                                            animationDuration: `${bubble.duration ?? 4.8}s`,
                                        }}
                                    >
                                        {bubble.text}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="jcalm-dots">
                {SLIDES.map((_, i) => (
                    <button key={i} className={`jcalm-dot ${i === active ? 'jcalm-dot-active' : ''}`} onClick={() => goTo(i)} aria-label={`Slide ${i + 1}`} />
                ))}
            </div>
        </div>
    );
}

export default function JCalHome() {
    const currentYear = new Date().getFullYear();

    useLayoutEffect(() => {
        const jcalBg = '#ffffff';
        const prevHtml = document.documentElement.style.background;
        const prev = document.body.style.background;
        const root = document.getElementById('root');
        const prevRoot = root?.style.background;
        document.documentElement.style.setProperty('background', jcalBg, 'important');
        document.body.style.setProperty('background', jcalBg, 'important');
        if (root) root.style.setProperty('background', jcalBg, 'important');
        return () => {
            document.documentElement.style.background = prevHtml;
            document.body.style.background = prev;
            if (root) root.style.background = prevRoot || '';
        };
    }, []);

    return (
        <div className="jcalm jcalm-v2" style={{ background: '#ffffff' }}>

            <div className="jcalm-announce-banner">
                JCAL.la is now&nbsp;<strong>JCAL.app</strong>
            </div>

            {/* Hero — logo dominant, centered */}
            <section className="jcalm-v2-hero" style={{ background: '#ffffff' }}>
                <img src={`${process.env.PUBLIC_URL || ''}/jcal-logo-transparent.png?v=transparent-bg-1`} alt="JCAL.app" className="jcalm-v2-logo" />
                <img src={`${process.env.PUBLIC_URL || ''}/jcal-app-transparent-logo.png?v=theme-blue-dark-only-1`} alt="JCAL.app" className="jcalm-v2-wordmark-logo" />
                <p className="jcalm-v2-cities">NYC &nbsp;·&nbsp; LA</p>
                <p className="jcalm-v2-tagline">The Beautiful Balagan of Jewish Life. Organized, and in your Pocket.</p>
                <p className="jcalm-v2-sub">JCAL.app follows the major Jewish WhatsApp event groups so you don’t have to.</p>

                {/* Store buttons right up top */}
                <div className="jcalm-store-row jcalm-store-row-centered">
                    <a href={APP_STORE_LINK} className="jcalm-badge-link" target="_blank" rel="noopener noreferrer">
                        <img src={`${process.env.PUBLIC_URL || ''}/app-store-badge.svg`} alt="Download on the App Store" className="jcalm-badge-img" />
                    </a>
                    <a href={PLAY_STORE_LINK} className="jcalm-badge-link" target="_blank" rel="noopener noreferrer">
                        <img src={`${process.env.PUBLIC_URL || ''}/google-play-badge.png`} alt="Get it on Google Play" className="jcalm-badge-img" />
                    </a>
                </div>
            </section>

            {/* Divider */}
            <div className="jcalm-v2-divider">
                <span>Features</span>
            </div>

            {/* Carousel — fills the width */}
            <Carousel />

            {/* Features */}
            <section className="jcalm-features jcalm-features-centered">
                <div className="jcalm-feature">
                    <div className="jcalm-feature-icon-wrap">🗽<span className="jcalm-icon-sep"> / </span>🌴</div>
                    <h3>20+ events added every week</h3>
                    <p>Covering the Jewish communities in New York City 🗽 and Los Angeles 🌴</p>
                </div>
                <div className="jcalm-feature jcalm-feature-rabbi">
                    <div className="jcalm-rabbi-widget">
                        <img src={`${process.env.PUBLIC_URL || ''}/rabbi-head.png`} alt="Rabbi" className="jcalm-rabbi-img" />
                    </div>
                    <h3>Meet Moishe</h3>
                    <p>Your RabbAI guide to Jewish life. Ask about events, get curated picks</p>
                </div>
                <div className="jcalm-feature">
                    <div className="jcalm-feature-icon-wrap">🔒</div>
                    <h3>Security for the Community</h3>
                    <p>Access limited to members of trusted WhatsApp groups. Or via invite from an existing user. To protect the safety of our community ✡️</p>
                </div>
            </section>

            {/* Second CTA at bottom */}
            <section className="jcalm-v2-bottom-cta">
                <p className="jcalm-v2-bottom-cta-line">Ready to join your Jewish community?</p>
                <div className="jcalm-store-row jcalm-store-row-centered">
                    <a href={APP_STORE_LINK} className="jcalm-badge-link" target="_blank" rel="noopener noreferrer">
                        <img src={`${process.env.PUBLIC_URL || ''}/app-store-badge.svg`} alt="Download on the App Store" className="jcalm-badge-img" />
                    </a>
                    <a href={PLAY_STORE_LINK} className="jcalm-badge-link" target="_blank" rel="noopener noreferrer">
                        <img src={`${process.env.PUBLIC_URL || ''}/google-play-badge.png`} alt="Get it on Google Play" className="jcalm-badge-img" />
                    </a>
                </div>
            </section>

            <footer className="jcalm-footer">
                <p>© {currentYear} HappTrak, LLC. All rights reserved.</p>
                <a href="/privacy">Privacy Policy</a>
                <a href="/jcal/delete-account">Data Deletion</a>
            </footer>
        </div>
    );
}
